import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import dayjs from "dayjs";
import {
  generalResearch,
  medpgmpc,
  ricAnapres,
} from "../../../../../../networking";
import { AllertMessage } from "./MedintAgendaForm/MedintAgendaFormDetail";
import {
  RHFAutocomplete,
  RHFTextField,
} from "../../../../../RHFComponents/RHFComponents";

type Props = {
  open: boolean;
  onClose: () => void;
  selectedRow?: any;
  action: "insert" | "edit";
  specialita?: string;
  prestazione?: string;
  idMedpgmp: string;
  onSaved: () => void;
};

type GestioneCombinateDettaglioForm = {
  codiceSiss: string;
  codiceIstanzaCombinata: string;
  durata: string;
};

const GestioneCombinateModalDetails = ({
  open,
  onClose,
  selectedRow,
  action,
  specialita,
  prestazione,
  idMedpgmp,
  onSaved,
}: Props) => {
  const methods = useForm<GestioneCombinateDettaglioForm>({
    defaultValues: {
      codiceSiss: "",
      codiceIstanzaCombinata: "",
      durata: "",
    },
  });

  const { control, reset, getValues, handleSubmit } = methods;

  // Dati restituiti da RicAnapres
  const [codSissOptions, setCodSissOptions] = useState<any[]>([]);
  //Dati restituiti da generalResearch
  const [istanzaCombinataOptions, setIstanzaCombinataOptions] = useState<any[]>(
    [],
  );

  const [loading, setLoading] = useState<boolean>(false);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");
  const [pendingSubmit, setPendingSubmit] =
    useState<GestioneCombinateDettaglioForm | null>(null);

  /*
   * Recupera i codici SISS selezionabili in base a specialità e prestazione
   * I dati vengono mappati in modo uniforme { value, label, _raw }
   * così l'RHFAutocomplete lavora sempre nello stesso modo.
   */
  const getCodSissData = async () => {
    try {
      const response = await ricAnapres({
        aziendaPrenotazione: "MEDITEL",
        dataRif: dayjs().format("YYYY-MM-DD"),
        codSpecia: specialita ?? "",
        codAnapre: prestazione ?? "",
        codAnapres: "",
        testValidita: "S",
        flDisattivati: "S",
      });

      const raw = response?.data?.dati?.codiciSISS;
      const data = Array.isArray(raw) ? raw : [];

      const mapped = data.map((item: any) => ({
        value: item?.CODANAPRES ?? "",
        label: `${item?.CODANAPRES ?? ""} - ${item?.DESANAPRES ?? ""}`.trim(),
        _raw: item,
      }));

      setCodSissOptions(mapped);
    } catch (error) {
      console.error(error);
      setCodSissOptions([]);
    }
  };

  /**
   * Recupera l'elenco delle istanze combinate dalla tabella ISTCOM.
   * Anche qui uniformo i dati in { value, label, _raw }.
   */
  const getCodIstanzaCombinata = async () => {
    try {
      const response = await generalResearch({
        tabella: "ISTCOM",
        codice: "",
        filtro: "",
        applicazione: "",
        attivi: "1",
      });

      const raw = response?.data?.dati;
      const data = Array.isArray(raw) ? raw : [];

      const mapped = data.map((item: any) => ({
        value: item?.codice ?? "",
        label: `${item?.codice ?? ""} - ${item?.descrizione ?? ""}`.trim(),
        _raw: item,
      }));

      setIstanzaCombinataOptions(mapped);
    } catch (error) {
      console.error(error);
      setIstanzaCombinataOptions([]);
    }
  };

  /**
   * In edit i campi "codice SISS" e "codice istanza combinata"
   * restano disabilitati per richiesta del cliente
   * Non serve la lista completa, perciò costruisco una option locale con la row selesionata
   * così l'autocomplete riesce comunque a mostrare il valore corrente
   */
  const selectedCodSissOption = useMemo(
    () =>
      selectedRow?.codiceSISS
        ? [
            {
              value: selectedRow.codiceSISS,
              label: selectedRow.codiceSISS,
            },
          ]
        : [],
    [selectedRow],
  );

  /*
   * Option fallback per il campo istanza combinata in edit.
   * Mostro "codice - descrizione" anche se non ho caricato tutta la lista.
   */
  const selectedIstanzaOption = useMemo(
    () =>
      selectedRow?.codIstanzaCombinata || selectedRow?.descrIstanzaCombinata
        ? [
            {
              value: selectedRow?.codIstanzaCombinata ?? "",
              label:
                `${selectedRow?.codIstanzaCombinata ?? ""} - ${selectedRow?.descrIstanzaCombinata ?? ""}`.trim(),
            },
          ]
        : [],
    [selectedRow],
  );

  /*
   * Se sono in edit uso le option locali costruite dalla row selezionata.
   * Se sono in insert uso invece le liste complete caricate da API.
   */
  const finalCodSissOptions =
    action === "edit" ? selectedCodSissOption : codSissOptions;

  const finalIstanzaOptions =
    action === "edit" ? selectedIstanzaOption : istanzaCombinataOptions;

  /*
   * Apro il modale e in base all'azione eseguo le operazioni necessarie.
  In inserimento carico tutte le liste selezionabili e resetto il form a vuoto.
  In modifica valorizzo il form con i dati della row selezionata precedentemente
   */
  useEffect(() => {
    if (!open) return;

    if (action === "insert") {
      getCodSissData();
      getCodIstanzaCombinata();
      reset({
        codiceSiss: "",
        codiceIstanzaCombinata: "",
        durata: "",
      });
    }

    if (action === "edit") {
      reset({
        codiceSiss: selectedRow?.codiceSISS ?? "",
        codiceIstanzaCombinata: selectedRow?.codIstanzaCombinata ?? "",
        durata: selectedRow?.durata ?? "",
      });
    }
  }, [open, reset, selectedRow, action, specialita, prestazione]);

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
      setPendingSubmit(null);
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  const handleAskSubmit = (dataValues: GestioneCombinateDettaglioForm) => {
    setPendingSubmit(dataValues);
    setSeverity("warning");
    setMessage(
      action === "insert"
        ? "Confermi l'inserimento dell'istanza combinata?"
        : "Confermi la modifica dell'istanza combinata?",
    );
    setShowAllert(true);
  };

  const handleClose = () => {
    reset({
      codiceSiss: "",
      codiceIstanzaCombinata: "",
      durata: "",
    });
    setPendingSubmit(null);
    setShowAllert(false);
    setSeverity("warning");
    setMessage("");
    onClose();
  };

  /**
   * Funzione di submit che vale sia per l'inserimento che per la modifica.
   * In base al valore di action decido se si tratta di un inserimento o modifica.
   * In edit passo anche il prgint (ovvero l'id medpgmpc) della riga combinata da aggiornare.
   */
  const onSubmit = async (dataValues: GestioneCombinateDettaglioForm) => {
    setLoading(true);

    try {
      const response = await medpgmpc({
        crud: action === "insert" ? "c" : "u",
        ...(action === "edit" && {
          prgint:
            selectedRow?.prgint ?? selectedRow?._raw?.MEDPGMPC_PRGINT ?? "",
        }),
        dati: {
          medpgmpc: {
            MEDPGMP_PRGINT: idMedpgmp ?? "",
            ISTCOM_CODICE: dataValues?.codiceIstanzaCombinata ?? "",
            ANAPRES_SISS_COD: dataValues?.codiceSiss ?? "",
            MEDPGMPC_DURATA: dataValues?.durata ?? "",
            MEDPGMPC_TIMESTAMP: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            MEDPGMPC_PID_ACU: "0",
          },
        },
      });

      if (response?.data?.esito === "OK") {
        setSeverity("success");
        setMessage(
          (response?.data?.messaggio ?? action === "insert")
            ? "Istanza combinata inserita correttamente"
            : "Istanza combinata aggiornata correttamente",
        );
        setShowAllert(true);
        setPendingSubmit(null);

        setTimeout(() => {
          handleClose();
        }, 1200);
        onSaved();
      } else {
        setSeverity("error");
        setMessage(
          response?.data?.messaggio ||
            "Errore durante il salvataggio dell'istanza combinata",
        );
        setShowAllert(true);
      }
    } catch (error) {
      console.error(error);
      setSeverity("error");
      setMessage("Errore durante il salvataggio dell'istanza combinata");
      setShowAllert(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AllertMessage
        dataToSend={pendingSubmit}
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={async (data: GestioneCombinateDettaglioForm) => {
          if (loading) return;

          if (severity === "warning") {
            await onSubmit(data);
            return;
          }

          setShowAllert(false);
        }}
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <Modal
        open={open}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          if (reason === "escapeKeyDown") return;
          handleClose();
        }}
      >
        <Box
          sx={{
            maxHeight: "90vh",
            width: "50%",
            overflow: "auto",
            p: 2,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              mb: 2,
              color: "var(--text-primary, rgba(0, 0, 0, 0.87))",
              fontFamily: "Rubik",
              fontSize: "16px",
              fontStyle: "normal",
              lineHeight: "24px",
              letterSpacing: "-0.16px",
            }}
          >
            Gestione Combinate
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <RHFAutocomplete
                name="codiceSiss"
                control={control}
                label="Codice SISS"
                options={finalCodSissOptions}
                fullWidth
                size="small"
                getOptionLabel={(option: any) => option?.label ?? ""}
                mapIn={(value, opts) =>
                  opts.find((o: any) => String(o.value) === String(value)) ??
                  null
                }
                mapOut={(option: any) => option?.value ?? ""}
                disabled={action === "edit"}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <RHFAutocomplete
                name="codiceIstanzaCombinata"
                control={control}
                label="Codice Istanza Combinata"
                options={finalIstanzaOptions}
                fullWidth
                size="small"
                getOptionLabel={(option: any) => option?.label ?? ""}
                mapIn={(value, opts) =>
                  opts.find((o: any) => String(o.value) === String(value)) ??
                  null
                }
                mapOut={(option: any) => option?.value ?? ""}
                disabled={action === "edit"}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <RHFTextField
                name="durata"
                control={control}
                label="Durata"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Button onClick={handleClose} variant="outlinedCustom">
                Indietro
              </Button>
              <Button
                onClick={handleSubmit(handleAskSubmit)}
                variant="containedCustom"
              >
                Conferma
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default GestioneCombinateModalDetails;
